// utils/referralSystem.js - FIXED VERSION

import { doc, updateDoc, getDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";

/**
 * ✅ FIXED: Validate referral code exists and is valid
 */
export const validateReferralCode = async (referralCode) => {
  try {
    console.log("🔍 Validating referral code:", referralCode);
    
    if (!referralCode || referralCode.trim().length === 0) {
      console.log("❌ Empty referral code");
      return { valid: false, error: "Referral code is required" };
    }
    
    // Clean and normalize the code
    const cleanCode = referralCode.trim().toUpperCase();
    console.log("🧹 Cleaned code:", cleanCode);
    
    // ✅ FIX: Get ALL documents and search manually (more reliable)
    const investorsRef = collection(db, "Investors");
    console.log("📡 Fetching all investors...");
    
    const snapshot = await getDocs(investorsRef);
    console.log("📊 Total documents found:", snapshot.size);
    
    let foundUser = null;
    
    snapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      console.log("🔍 Checking user:", {
        uid: docSnapshot.id,
        fullName: data.fullName,
        referralCode: data.referralCode,
        email: data.emailAddress || data.email
      });
      
      // Check if this user has the referral code we're looking for
      if (data.referralCode && data.referralCode.toUpperCase() === cleanCode) {
        foundUser = {
          uid: docSnapshot.id,
          data: data
        };
        console.log("✅ FOUND MATCH:", foundUser);
      }
    });
    
    if (foundUser) {
      const result = { 
        valid: true, 
        referrerName: foundUser.data.fullName,
        referrerEmail: foundUser.data.emailAddress || foundUser.data.email,
        referrerId: foundUser.uid
      };
      console.log("🎉 Validation successful:", result);
      return result;
    }
    
    console.log("❌ No match found for code:", cleanCode);
    return { valid: false, error: "Invalid referral code" };
    
  } catch (error) {
    console.error("❌ Error validating referral code:", error);
    return { valid: false, error: "Error validating referral code: " + error.message };
  }
};

/**
 * Generate a unique referral code
 */
export const generateReferralCode = (fullName, userId) => {
  const namePrefix = fullName.replace(/\s+/g, '').toUpperCase().slice(0, 3);
  const randomSuffix = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `REF${namePrefix}${randomSuffix}`;
};

/**
 * Check if referral code is unique
 */
export const isReferralCodeUnique = async (code) => {
  try {
    const investorsRef = collection(db, "Investors");
    const snapshot = await getDocs(investorsRef);
    
    let isUnique = true;
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.referralCode && data.referralCode.toUpperCase() === code.toUpperCase()) {
        isUnique = false;
      }
    });
    
    return isUnique;
  } catch (error) {
    console.error("Error checking referral code uniqueness:", error);
    return false;
  }
};

/**
 * Generate unique referral code for user
 */
export const createUniqueReferralCode = async (fullName, userId) => {
  let attempts = 0;
  let code;
  
  do {
    code = generateReferralCode(fullName, userId);
    attempts++;
  } while (!(await isReferralCodeUnique(code)) && attempts < 10);
  
  if (attempts >= 10) {
    code = `REF${Date.now().toString().slice(-6)}`;
  }
  
  return code;
};

/**
 * ✅ FIXED: Process referral when new user signs up
 */
// utils/referralSystem.js - FIXED processReferral function

/**
 * ✅ FIXED: Process referral when new user signs up
 * This function now ONLY validates and updates the referrer's stats
 * The new user's referral fields will be set in UserDetails component
 */
export const processReferral = async (newUserId, referralCode, newUserEmail, newUserName) => {
  try {
    if (!referralCode) {
      console.log("⚠️ No referral code provided");
      return null;
    }
    
    console.log("🔄 Processing referral:", {
      newUserId,
      referralCode: referralCode.trim(),
      newUserEmail,
      newUserName
    });
    
    // First validate the referral code
    const validation = await validateReferralCode(referralCode.trim());
    console.log("🔍 Referral validation result:", validation);
    
    if (!validation.valid) {
      throw new Error(`Invalid referral code: ${validation.error}`);
    }
    
    const referrerId = validation.referrerId;
    const referrerData = {
      fullName: validation.referrerName,
      email: validation.referrerEmail
    };
    
    console.log("✅ Found valid referrer:", { referrerId, referrerData });
    
    // Get current referrer stats
    const referrerRef = doc(db, "Investors", referrerId);
    const referrerDoc = await getDoc(referrerRef);
    
    if (!referrerDoc.exists()) {
      throw new Error("Referrer document not found");
    }
    
    const currentStats = referrerDoc.data()?.referralStats || { 
      totalReferrals: 0, 
      successfulReferrals: 0, 
      totalBonusEarned: 0 
    };
    
    console.log("📊 Current referrer stats:", currentStats);
    
    // ✅ Update referrer's stats
    await updateDoc(referrerRef, {
      "referralStats.totalReferrals": currentStats.totalReferrals + 1,
      "referralStats.lastReferral": {
        userId: newUserId,
        email: newUserEmail,
        name: newUserName,
        date: new Date(),
        processedAt: new Date()
      }
    });
    
    console.log("✅ Updated referrer stats successfully");
    
    // ✅ Return referral info for the new user document
    const result = {
      referrerId: referrerId,
      referrerName: referrerData.fullName,
      referrerEmail: referrerData.email,
      referralCode: referralCode.trim().toUpperCase(),
      success: true
    };
    
    console.log("🎉 Referral processing completed successfully:", result);
    return result;
    
  } catch (error) {
    console.error("❌ Error processing referral:", error);
    throw error;
  }
};

/**
 * Get pending referrals for a specific referrer
 */
export const getPendingReferrals = async (referrerId) => {
  try {
    const investorsRef = collection(db, "Investors");
    const snapshot = await getDocs(investorsRef);
    
    const pendingReferrals = [];
    snapshot.forEach(doc => {
      const data = doc.data();
      if (data.referredBy === referrerId && data.referralBonusProcessed === false) {
        pendingReferrals.push({
          userId: doc.id,
          email: data.emailAddress || data.email,
          fullName: data.fullName,
          referralDate: data.referralDate,
          referredByCode: data.referredByCode
        });
      }
    });
    
    return pendingReferrals;
  } catch (error) {
    console.error("Error getting pending referrals:", error);
    return [];
  }
};

/**
 * Get all pending referrals across all users
 */
export const getAllPendingReferrals = async () => {
  try {
    const investorsRef = collection(db, "Investors");
    const snapshot = await getDocs(investorsRef);
    
    const pendingReferrals = [];
    snapshot.forEach(doc => {
      const data = doc.data();
      if (data.referredBy && data.referralBonusProcessed === false) {
        pendingReferrals.push({
          userId: doc.id,
          email: data.emailAddress || data.email,
          fullName: data.fullName,
          referralDate: data.referralDate,
          referredBy: data.referredBy,
          referredByCode: data.referredByCode,
          referredByName: data.referredByName
        });
      }
    });
    
    return pendingReferrals;
  } catch (error) {
    console.error("Error getting all pending referrals:", error);
    return [];
  }
};

/**
 * ✅ ADDED: Assign referral code to user (for dashboard compatibility)
 */
export const assignReferralCode = async (userId, fullName) => {
  try {
    const userRef = doc(db, "Investors", userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error("User not found");
    }
    
    const userData = userDoc.data();
    
    // Skip if user already has a referral code
    if (userData.referralCode) {
      console.log("✅ User already has referral code:", userData.referralCode);
      return userData.referralCode;
    }
    
    // Generate new code
    const referralCode = await createUniqueReferralCode(fullName, userId);
    console.log("🆕 Generated new referral code:", referralCode);
    
    // Update user document
    await updateDoc(userRef, {
      referralCode: referralCode,
      referralStats: {
        totalReferrals: 0,
        successfulReferrals: 0,
        totalBonusEarned: 0,
        createdAt: new Date()
      }
    });
    
    console.log("✅ Referral code assigned successfully");
    return referralCode;
  } catch (error) {
    console.error("❌ Error assigning referral code:", error);
    throw error;
  }
};

/**
 * ✅ ADDED: Mark referral as processed (for admin dashboard)
 */
export const markReferralAsProcessed = async (userId) => {
  try {
    await updateDoc(doc(db, "Investors", userId), {
      referralBonusProcessed: true,
      referralProcessingStatus: "completed",
      referralProcessedDate: new Date()
    });
    console.log("✅ Marked referral as processed for user:", userId);
  } catch (error) {
    console.error("❌ Error marking referral as processed:", error);
    throw error;
  }
};